<template>
  <div class="interviewPage flex flex-col flex-1 h-full relative z-10" :style="{ maxHeight: 'calc(100vh - 170px)' }">
    <!-- Chat Container -->
    <div class="flex flex-col flex-1 overflow-hidden relative bg-white bg-opacity-10 backdrop-blur-sm rounded-xl border border-white border-opacity-20 shadow-xl">
      <!-- Info Button -->
      <div class="absolute top-3 right-3 z-20">
        <button @click="showCode" class="rounded-full bg-blue-600 bg-opacity-70 hover:bg-opacity-100 p-2 focus:outline-none text-white shadow-lg transition-all duration-300 hover:scale-105">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
            class="feather feather-info">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
        </button>
      </div>
      
      <!-- Chat History -->
      <div id="chat-history" ref="chatHistoryDiv" 
        class="p-4 overflow-y-auto flex-1 bg-white bg-opacity-95 rounded-t-xl"
        :style="{ maxHeight: 'calc(100vh - 120px)' }">
        <!-- Mensajes normales de texto y mensajes multimedia -->
<template v-for="(message, index) in chatHistory" :key="index">
  <!-- Si es un mensaje multimedia -->
  <div v-if="message.mediaType" :class="['mb-4 flex', message.type === 'user' ? 'justify-end' : 'justify-start']">
    <div :class="[
      'px-4 py-3 rounded-xl shadow-md max-w-3/4', 
      message.type === 'user' 
        ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-tr-none'
        : 'bg-white border border-blue-100 rounded-tl-none'
    ]">
      <MediaMessage 
        :mediaUrl="getMediaUrl(message.mediaId)" 
        :mediaType="message.mediaType"
        :transcription="message.transcription || ''"
        :mediaData="message.mediaData || ''"
        :mediaChunks="message.mediaChunks || []"
        :totalChunks="message.totalChunks || 0"
        :mimeType="message.mimeType || ''"
        :encoding="message.encoding || 'base64'"
        :mediaId="message.mediaId || ''"
        :duration="message.duration || 0"
        :isLarge="message.isLarge || false"
        :sizeMB="message.sizeMB || 0"
        :storeMethod="message.storeMethod || 'inline'"
      />
      <div class="flex justify-end mt-1">
        <span :class="[
          'text-xs italic', 
          message.type === 'user' ? 'text-blue-100' : 'text-gray-500'
        ]">
          {{ formatTime(message.timestamp) }}
        </span>
      </div>
    </div>
  </div>
  <!-- Mensajes de texto normales -->
  <ChatMessage v-else :message="message" />
</template>
      </div>
    </div>
    
    <!-- Analysis Buttons -->
    <div v-if="showAnalysisButtons" class="flex justify-center mt-3">
      <button v-if="!hasAnalysis && !isAnalyzing" @click="analyzeChat"
        class="bg-gradient-to-r from-yellow-400 to-yellow-500 text-blue-900 font-bold py-3 px-6 rounded-full transition-all duration-300 hover:shadow-lg hover:scale-105 transform hover:-translate-y-1 shadow-md">
        Analizar Entrevista
        <span v-if="timer" class="ml-1 bg-white bg-opacity-30 rounded-full px-2 py-0.5">{{ timer }}</span>
      </button>
      
      <button v-if="hasAnalysis" @click="$emit('goToAnalysis')"
        class="bg-gradient-to-r from-green-400 to-green-500 text-white font-bold py-3 px-6 rounded-full transition-all duration-300 hover:shadow-lg hover:scale-105 transform hover:-translate-y-1 shadow-md">
        Ver Análisis
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 inline-block ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
      </button>
      
      <button v-if="isAnalyzing"
        class="bg-blue-600 bg-opacity-50 text-white font-bold py-3 px-6 rounded-full shadow-md opacity-80 cursor-not-allowed">
        <svg class="animate-spin -ml-1 mr-2 h-5 w-5 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Analizando
        <span v-if="timer" class="ml-1 bg-white bg-opacity-30 rounded-full px-2 py-0.5">{{ timer }}</span>
      </button>
    </div>
    
    <!-- Message Input -->
    <div v-if="status !== 'Finished'" class="flex items-center p-3 bg-white bg-opacity-20 backdrop-blur-sm rounded-b-xl border-t border-white border-opacity-20 mt-1">
      <textarea
        v-model="newMessage"
        placeholder="Escribe un mensaje... empieza con 'Hola' para iniciar la conversación"
        required
        :disabled="disableChat"
        @keydown.enter.prevent="sendMessage"
        class="flex-1 bg-white bg-opacity-90 border-0 rounded-xl p-3 mr-2 focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none overflow-y-auto shadow-inner"
        :style="{ maxHeight: '150px' }"
        ref="messageInput"
      ></textarea>
      
      <!-- Botón de grabación de media 
      <div class="mx-1" v-if="!disableChat">
        <MediaRecorder @media-recorded="handleMediaRecording" />
      </div>
      -->
      
      <button type="button" @click="sendMessage"
        :class="{'bg-blue-300 cursor-not-allowed': disableChat || !newMessage.trim(), 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:shadow-lg transform hover:scale-105': !disableChat && newMessage.trim()}"
        class="p-3 rounded-full text-white focus:outline-none flex items-center justify-center transition-all duration-300 shadow-md"
        :disabled="disableChat || !newMessage.trim()">
        <span v-if="loading" class="animate-spin">
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M12 6v6l4 2"></path>
          </svg>
        </span>
        <span v-else>
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="22" y1="2" x2="11" y2="13"></line>
            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
          </svg>
        </span>
      </button>
    </div>
    
    <!-- Status Messages -->
    <div v-if="disableChat && hasAnalysis && !isAnalyzing" class="text-center mt-3 p-3 bg-blue-900 bg-opacity-50 backdrop-blur-sm rounded-xl text-white border border-white border-opacity-20">
      <p class="font-medium">La entrevista ha concluido. ¡Gracias por tu participación!</p>
    </div>
    
    <div v-if="disableChat && isAnalyzing" class="text-center mt-3 p-3 bg-yellow-500 bg-opacity-20 backdrop-blur-sm rounded-xl text-white border border-yellow-500 border-opacity-30">
      <p class="font-medium">Por favor espere. La entrevista se está analizando...</p>
    </div>
    
    <!-- Interview Code Dialog -->
    <CodeDialog v-if="showCodePopUp" :interviewLink="interviewLink" :intCode="chatSessionId" @close="closeCode" />
  </div>
</template>

<script>
import axios from 'axios';
import { auth, db } from '@/firebase';
import { doc, collection, getDoc } from 'firebase/firestore';
import ChatMessage from './ChatMessage.vue';
import CodeDialog from './CodeDialog.vue';
//import MediaRecorder from './multimedia/MediaRecorder.vue';
import MediaMessage from './multimedia/MediaMessage.vue';

export default {
  name: 'InterviewChat',
  components: {
    ChatMessage,
    CodeDialog,
    //MediaRecorder,
    MediaMessage
  },
  emits: ['goToAnalysis'],
  props: {
    chatSessionId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      newMessage: '',
      chatHistory: [],
      disableChat: false,
      loading: false,
      chatHistoryDiv: null,
      status: 'Not Started',
      hasAnalysis: false,
      showAnalysisButtons: false,
      isAnalyzing: false,
      timer: 0,
      showCodePopUp: false,
      isPractice: true,
      mediaMessages: {}, // Para almacenar las URLs temporales de los mensajes multimedia
      isUploadingMedia: false,
    };
  },
  computed: {
    interviewLink() {
      return `https://interbot.mx?code=${this.chatSessionId}`
    },
  },
  async mounted() {
    await this.fetchChatHistory();
    // Hacer scroll al último mensaje al cargar
    this.scrollToBottom();
  },
  methods: {
    getMediaUrl(mediaId) {
      return this.mediaMessages[mediaId] || '';
    },
    formatTime(timestamp) {
      const date = new Date(timestamp);
      return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    showCode() {
      this.showCodePopUp = true;
    },
    closeCode() {
      this.showCodePopUp = false;
    },
    async handleMediaRecording(mediaData) {
      try {
        this.isUploadingMedia = true;
        
        // Crear un ID único para este mensaje multimedia
        const mediaId = 'media_' + Date.now();
        
        // Crear una URL temporal para la previsualización inmediata
        const mediaUrl = URL.createObjectURL(mediaData.file);
        this.mediaMessages[mediaId] = mediaUrl;
        
        // Crear mensaje de usuario con el contenido multimedia
        const userMessage = {
          type: 'user',
          mediaType: mediaData.type,
          mediaId: mediaId,
          timestamp: new Date().toISOString(),
        };
        
        // Añadir a la historia del chat
        this.chatHistory.push(userMessage);
        
        // Asegurar que el chat se desplaza hacia abajo
        this.scrollToBottom();
        
        // Convertir el archivo a Base64 para enviarlo al servidor
        const reader = new FileReader();
        console.log("Tipo de archivo para FileReader:", mediaData.file.type);
        
        // Agregar manejadores de error para el FileReader
        reader.onerror = () => {
          console.error("Error en FileReader:", reader.error);
          this.chatHistory.push({
            type: 'system',
            text: "Error al procesar el archivo multimedia. Por favor, intenta con otro archivo.",
            timestamp: new Date().toISOString(),
            isError: true
          });
          this.isUploadingMedia = false;
        };
        
        // Comenzar la lectura del archivo
        reader.readAsDataURL(mediaData.file);
        reader.onload = async () => {
          console.log("FileReader completado, longitud de datos:", reader.result ? reader.result.length : 0);
          
          // Verificar que reader.result contiene datos y es un formato data URL válido
          if (!reader.result || typeof reader.result !== 'string' || !reader.result.includes('base64,')) {
            console.error("Error: Formato de datos inválido en FileReader");
            throw new Error("Formato de datos inválido");
          }
          
          const parts = reader.result.split('base64,');
          if (parts.length < 2) {
            console.error("Error: No se encontró el marcador base64 en los datos");
            throw new Error("Formato de datos inválido: falta marcador base64");
          }
          
          const base64data = parts[1]; // Extraer solo la parte base64 del data URL
          console.log("Longitud de datos base64 extraídos:", base64data ? base64data.length : 0);
          
          // Enviar el archivo al servidor
          try {
            this.loading = true;
            
            // Log para depuración
            console.log("Enviando multimedia al servidor:", {
              tipo: mediaData.type,
              duración: mediaData.duration,
              mimeType: mediaData.file.type,
              tamaño: base64data ? base64data.length : 0,
              chatSessionId: this.chatSessionId
            });
            
            // Validar que los datos base64 no sean null o vacíos
            if (!base64data || base64data.trim() === '') {
              console.error("Error: Base64 data es vacío o inválido");
              throw new Error("Base64 data is empty or invalid");
            }
            
            // Verificar tamaño para advertir sobre posibles problemas
            const fileSizeMB = (base64data.length * 0.75) / (1024 * 1024); // Estimación aproximada
            console.log(`Tamaño estimado del archivo: ${fileSizeMB.toFixed(2)} MB`);
            
            if (fileSizeMB > 10) {
              console.warn(`Archivo grande (${fileSizeMB.toFixed(2)} MB). Podría haber problemas.`);
            }
            
            // Objeto de datos a enviar
            const requestData = {
              mediaData: base64data,
              mediaType: mediaData.type,
              mimeType: mediaData.file.type,
              duration: mediaData.duration,
              chatSessionId: this.chatSessionId,
              status: this.status === "Not Started" ? "In progress" : this.status
            };
            
            // Configurar timeout más largo para archivos grandes
            const timeoutMs = Math.max(10000, fileSizeMB * 1000);
            
            // Enviar la grabación al servidor
            const response = await axios.post('/api/chat-media', requestData, {
              timeout: timeoutMs,
              headers: {
                'Content-Type': 'application/json'
              }
            });
            
            // Actualizar el historial del chat con la respuesta
            this.chatHistory = response.data.chatHistory;
            
            // Verificar si se debe deshabilitar el chat
            const lastMessage = this.chatHistory[this.chatHistory.length - 1];
            if (lastMessage && lastMessage.text && lastMessage.text.includes("SYSTEM_ACTION: DISABLE_CHAT")) {
              this.disableChat = true;
              this.status = "Finished";
              await this.fetchChatHistory();
              if (this.isPractice) {
                this.showAnalysisButtons = true;
              }
            }
            
          } catch (error) {
            console.error('Error enviando mensaje multimedia:', error);
            console.error('Detalles del error:', error.response ? error.response.data : 'No hay detalles');
            
            // Crear mensaje de error más informativo
            let errorMessage = 'Hubo un problema al procesar tu grabación. ';
            
            if (error.response) {
              if (error.response.status === 400) {
                errorMessage += 'El archivo puede ser demasiado grande o el formato no es compatible. ';
              } else if (error.response.status === 500) {
                errorMessage += 'Error en el servidor. ';
              }
              
              // Agregar detalles específicos si están disponibles
              if (error.response.data && error.response.data.error) {
                errorMessage += `Detalle: ${error.response.data.error}`;
              }
            } else if (error.request) {
              errorMessage += 'No se recibió respuesta del servidor. Comprueba tu conexión.';
            } else {
              errorMessage += 'Intenta con un archivo más pequeño o en otro formato.';
            }
            
            // Mostrar mensaje de error
            this.chatHistory.push({
              type: 'system',
              text: errorMessage,
              timestamp: new Date().toISOString(),
              isError: true
            });
            
            // Hacer scroll al mensaje de error
            this.scrollToBottom();
          } finally {
            this.loading = false;
          }
        };
        
      } catch (error) {
        console.error('Error preparando el archivo multimedia:', error);
      } finally {
        this.isUploadingMedia = false;
      }
    },
   async fetchChatHistory() {
        if (this.chatSessionId) {
           try {
              let token = null;
            let interviewRef;
              const interviewDocRef = doc(db, "interviews", this.chatSessionId);
              const docSnap = await getDoc(interviewDocRef);
                 if(docSnap.exists()){
                    this.isPractice = true;
                       interviewRef = interviewDocRef;
                 }else {
                  interviewRef = doc(collection(db, "company_interviews"), this.chatSessionId);
                   this.isPractice = false;
                 }
                 
                console.log("interviewRef: ", interviewRef)
                if(!this.isPractice && auth.currentUser){
                     token = await auth.currentUser.getIdToken()
                 }
                 console.log("token: ", token)
                  const response = await axios.get(`/api/chat/${this.chatSessionId}`, {
                    headers: { Authorization: this.isPractice ? null : `Bearer ${token}` }
                  });
                  if(!response.status === 200){
                       const errorData = response.data;
                         throw new Error(
                            `HTTP error! status: ${response.status} ${JSON.stringify(errorData)}`
                           );
                   }
                 this.chatHistory = response.data.chatHistory;
                  this.status = response.data.status;
                 this.disableChat = this.status === "Finished";
                 this.hasAnalysis = response.data.analysis;
                    if(this.isPractice){
                       this.showAnalysisButtons = this.disableChat;
                    }
                   else {
                        this.showAnalysisButtons = this.hasAnalysis;
                     }
                 // Hacer scroll al último mensaje después de cargar el historial
                this.scrollToBottom();
         } catch (error) {
            console.error('Error fetching chat history:', error);
       }
    }
},
    // Método para hacer scroll al último mensaje
    scrollToBottom() {
      this.$nextTick(() => {
        if (this.$refs.chatHistoryDiv) {
          this.$refs.chatHistoryDiv.scrollTop = this.$refs.chatHistoryDiv.scrollHeight;
        }
      });
    },
    
    async sendMessage() {
      if (this.newMessage.trim() !== '') {
        this.loading = true;
        const userMessage = {
          text: this.newMessage,
          type: 'user',
          timestamp: new Date().toISOString(),
        };
        this.chatHistory.push(userMessage);
        
        // Hacer scroll cuando se añade un mensaje
        this.scrollToBottom();
        
        const messageToSend = this.newMessage;
        this.newMessage = '';
        try {
          let statusToSend = this.status;
          if (this.status === "Not Started") {
            statusToSend = "In progress"
          }
          const response = await axios.post('/api/chat', {
            message: messageToSend,
            chatSessionId: this.chatSessionId,
            status: statusToSend,
          });
          const responseChatHistory = response.data.chatHistory;
          this.chatHistory = responseChatHistory;
          
          // Hacer scroll tras recibir respuesta
          this.scrollToBottom();
          
          const lastMessage = responseChatHistory[responseChatHistory.length - 1]
          if (lastMessage && lastMessage.text.includes("SYSTEM_ACTION: DISABLE_CHAT")) {
            this.disableChat = true;
            this.status = "Finished"
              await this.fetchChatHistory();
              if (this.isPractice) {
                 this.showAnalysisButtons = true;
                }
          }
          if (lastMessage && lastMessage.type === 'system' && this.status !== "Finished") {
            let chatDisplayingIndex = 0;
            let chatAccumulatedText = lastMessage.text;
            this.chatHistory[this.chatHistory.length - 1].text = "";
            const displayChar = () => {
              if (chatDisplayingIndex < chatAccumulatedText.length) {
                this.chatHistory[this.chatHistory.length - 1].text = chatAccumulatedText.substring(0, chatDisplayingIndex + 1);
                chatDisplayingIndex++;
                
                // Hacer scroll durante el efecto de escritura
                this.scrollToBottom();
                
                setTimeout(displayChar, 5, this.$refs.chatHistoryDiv.value);
              }
            };
            displayChar();
          }
          console.log('Last message:', lastMessage);
        } catch (error) {
          console.error('Error sending message:', error);
        } finally {
          this.loading = false;
        }
      }
    },
    async analyzeChat() {
      this.isAnalyzing = true;
      this.timer = 3;
      const timerInterval = setInterval(() => {
        this.timer--;
        if (this.timer <= 0) {
          clearInterval(timerInterval);
          this.timer = 0;
        }
      }, 1000);
      try {
        await axios.post('/api/analyze-chat', {
          chatSessionId: this.chatSessionId
        });
         await this.fetchChatHistory();
        this.hasAnalysis = true;
      } catch (error) {
        console.error("Error al analizar la entrevista", error)
      } finally {
        this.isAnalyzing = false;
        clearInterval(timerInterval);
      }
    },
  },
};
</script>